import {html, css, LitElement} from 'lit';
import {customElement, property, state} from 'lit/decorators.js';
import {unsafeHTML} from 'lit/directives/unsafe-html.js';
import {map, of} from 'rxjs';
import Decimal from './break_infinity';
import {FilterView} from './filter-view';
import global_css from './global-css';
import {Filterable} from './interfaces/filterable';
import {ri} from './runner-idle';
import {RxLitElement} from './rx-lit-element';
import {UnlockableLitElement} from './unlockable-lit-element';
import {formatTime} from './utils';

@customElement('ri-log')
export class Log extends FilterView {
  // #region Properties (10)

  public static override styles = [
    ...FilterView.styles,
    css`
      .bottom-container {
        display: flex;
        flex-direction: column-reverse;
        flex-wrap: nowrap;
        overflow: auto;
      }
      .bottom-container * {
        margin: 0px;
        z-index: 10000;
      }
      /* Stripes */
      .bottom-container *:nth-child(even) {
        background-color: rgb(var(--black, 0, 0, 0), 0.05);
      }
      .bottom-container *:nth-child(odd) {
        background-color: rgb(var(--white, 255, 255, 255), 0.1);
      }
    `,
  ];

  // public override filterablesBeforeShowFilter = -1;
  public override id = 'log';
  public override isIntersection = false;
  public override isPackery = false;
  public override name = 'Log';
  public override possibleFilters = new Set([
    {name: 'distance', displayName: 'Distance'},
    {name: 'combat', displayName: 'Combat'},
    {name: 'activity', displayName: 'Activity'},
    {name: 'spell', displayName: 'Spell'},
    {name: 'landmark', displayName: 'Landmarks'},
    {name: 'unlockable', displayName: 'Unlocks'},
    {name: 'levelup', displayName: 'Levelups'},
  ]);
  public override replacementEntryTag = 'ri-log-entry, ri-activity-log-entry';
  public override subtitle = 'Wait, what was that?';

  constructor() {
    super();
    this.filterables$ = ri.logEntries$.pipe(
      map((entries) => entries.slice(0, 1000))
    );
  }

  // #endregion Properties (10)
}

/**
 * A log entry for the in-game log.
 */
@customElement('ri-log-entry')
export class LogEntry extends LitElement implements Filterable {
  // #region Properties (9)

  public static override styles = [
    global_css,
    css`
      :host {
        width: 100%;
        // @ts-ignore
        content-visibility: auto;
      }
      .header {
        min-width: 64px;
      }
      .container,
      .header,
      .msg {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        flex-wrap: nowrap;
        white-space: nowrap;
      }
      .container,
      .container * {
        align-items: center;
      }
      .msg {
        width: 100%;
      }
      .msg.error {
        background-color: #f22; //rgb(var(--alert-color, 255, 0, 0));
        color: rgb(var(--white, 255, 255, 255));
        font-weight: bolder;
      }
    `,
  ];

  /** How long of a time period does this log refer to? */
  @property({type: Number}) public duration = 1;
  /** Which life (reincarnation) was this logged in? */
  @property({type: Number}) public life: Number = 0;
  /** The message this entry shows. */
  @property() public msg = '';
  /** When was this logged?  */
  @state() public tick: Decimal = new Decimal(0);

  public conditionString = '';
  public displayName = '';
  /** An ID for this log, for merging redundant entries. */
  public mergeID: string | undefined;
  public tags: Set<string> = new Set();

  // #endregion Properties (9)

  // #region Public Methods (1)

  public override render() {
    if (ri.settings.scrollLogToNew) setTimeout(() => this._scrollIntoView());
    return html`<div class="container">
      <div class="header">
        ${`${this.life > 0 ? `${this.life}-` : ''} ${formatTime(
          this.tick,
          true
        )}`}
      </div>
      <div class="msg ${this.tags.has('error') ? 'error' : ''}">
        ${unsafeHTML(this.msg)}
        <div class="expand"></div>
      </div>
      <div class="expand"></div>
    </div>`;
  }

  // #endregion Public Methods (1)

  // #region Protected Methods (1)

  /** Scroll until this is on the screen. */
  protected _scrollIntoView() {
    if (!this.parentElement) return;
    this.parentElement.scrollTop = this.parentElement.scrollHeight;
  }

  // #endregion Protected Methods (1)
}
